import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, inject, output, input, signal, effect, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogService } from 'primeng/dynamicdialog';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { SafeJsonPipe } from '../../pipes/safe-json.pipe';

import { ChatHeaderComponent } from './chat-header/chat-header.component';
import { ChatFooterComponent } from './chat-footer/chat-footer.component';
import { ChatMessagesListComponent } from './chat-messages-list/chat-messages-list.component';
import { DCConversationUserChatSettingsComponent } from './chat-settings/dc-conversation-userchat-settings.component';

import { MicSettings } from '@dataclouder/ngx-mic';

import { ChatMessage, IAgentCard, IConversationFlow, IConversationSettings, SimpleAgentTask } from '../../models/agent.models';
import { ConversationService } from '../../services/conversation.service';
import { EvaluationService } from '../../services/evaluation.service';
import { USER_DATA_EXCHANGE, UserDataExchangeAbstractService } from '../../models/user-data-exchange';
import { ChatEvent, ChatEventType } from '../../models/agent.models'; // Import the moved types
import { ChatUserSettings } from '@dataclouder/ngx-core';

@Component({
  selector: 'dc-chat',
  standalone: true,
  imports: [CommonModule, ChatHeaderComponent, ChatFooterComponent, ChatMessagesListComponent, DialogModule, ProgressBarModule, SafeJsonPipe],
  templateUrl: './chat-container.component.html',
  styleUrls: ['./chat-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService],
})
export class DCChatComponent implements OnInit, OnDestroy {
  // Services
  private dialogService = inject(DialogService);
  private conversationService = inject(ConversationService);
  private evaluationService = inject(EvaluationService);
  private userDataExchange = inject<UserDataExchangeAbstractService>(USER_DATA_EXCHANGE);
  @ViewChild(ChatFooterComponent) private chatFooterComponent!: ChatFooterComponent;
  // 📥 Inputs
  @Input() chatUserSettings: ChatUserSettings = this.userDataExchange.getUserChatSettings(); // Default to user data exchange
  @Input() conversationSettings!: IConversationSettings;
  @Input() conversationFlow: IConversationFlow | null = null;
  @Input() agentCard!: IAgentCard;
  // @Input() backgroundTasks: Record<ConversationEvents, SimpleAgentTask> | null = null;
  // 📤 Input Signals
  readonly taskOnUserMessage = input<SimpleAgentTask>();
  readonly taskOnAssistantMessage = input<SimpleAgentTask>();
  readonly parseDict = input<{ [key: string]: string }>({});
  // 📤 Outputs
  readonly sendMessage = output<ChatEvent>(); // Notifies about various events happening inside the chat
  readonly goalCompleted = output<void>(); // notifies when user completes goal (score reaches 100)
  // readonly wordClicked = output<WordData>(); // Replaced by sendMessage with ChatEventType.WordClicked

  // Signals States
  public messages = signal<ChatMessage[]>([]);
  // States
  public micSettings: MicSettings = { useWhisper: true, lang: 'en' };
  public isInfoVisible: boolean = false;
  public isAdmin: boolean = true;

  constructor() {
    // Subscribe to score updates using effect
    effect(() => {
      const score = this.evaluationService.getScore()();
      if (score >= 100) {
        this.goalCompleted.emit();
      }
    });

    // Effect to watch for word clicks from the service
    effect(() => {
      const clickedWordData = this.conversationService.getWordClickedSignal()();
      if (clickedWordData) {
        // Emit through the consolidated sendMessage output
        this.sendMessage.emit({ type: ChatEventType.WordClicked, payload: clickedWordData });
        // Optional: Reset the signal in the service if you only want to emit once per click
        // this.conversationService.notifyWordClicked(null);
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.conversationService.setDestroyed(false);

    if (this.conversationSettings) {
      await this.conversationService.initConversationWithSettings(this.conversationSettings);
    } else {
      await this.conversationService.initConversationWithAgentCard(this.agentCard, this.parseDict());
    }
    this.evaluationService.resetScore();
  }

  ngOnDestroy(): void {
    // Mark conversation as destroyed to prevent async operations
    this.conversationService.setDestroyed(true);

    // Ensure the microphone is stopped when the chat component is destroyed
    this.chatFooterComponent?.stopMic();
  }

  /**
   * Open chat settings dialog
   */
  public changeUserChatSettings(): void {
    this.dialogService
      .open(DCConversationUserChatSettingsComponent, {
        width: '90vw',
        header: 'Chat Settings',
        styleClass: 'settings-dialog',
        closable: true,
        closeOnEscape: true,
      })
      .onClose.subscribe(async () => {
        // TODO: Que hacer cuando cambie las configuraciones del usario?, creo que si existe en exchange no necesito guardarlo y leer desde el servicio.
      });
  }

  /**
   * Show debug info
   */
  public showInfo(): void {
    this.isInfoVisible = true;
  }

  /**
   * Restart conversation
   */
  public async restartConversation(conversation: IAgentCard | null = null): Promise<void> {
    if (conversation) {
      this.agentCard = conversation;
    }
    await this.ngOnInit();
  }
}
